import React, { useEffect, useMemo, useState } from 'react';
import { useT } from '../../i18n';
import {
  num,
  toUnits,
  toInput,
  formatUsd,
  formatAmount,
  formatPct,
  formatRatio,
  ratioBps,
  ratioTone,
  liquidationPrice,
} from './cdpHelpers';

const AmountField = ({ label, balanceLabel, value, onChange, onMax, maxLabel = 'MAX', suffix }) => (
  <div>
    <div className="mb-1 flex items-center justify-between gap-2">
      <span className="lb-label">{label}</span>
      <div className="flex items-center gap-2">
        {balanceLabel && <span className="text-xs text-white/45">{balanceLabel}</span>}
        {onMax && (
          <button
            type="button"
            onClick={onMax}
            className="rounded-md border border-white/15 bg-white/10 px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.12em] text-white/80 transition hover:bg-white/20 hover:text-white"
          >
            {maxLabel}
          </button>
        )}
      </div>
    </div>
    <div className="relative">
      <input type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)} className="lb-input p-3 pr-20" placeholder="0.0" />
      {suffix && <span className="notranslate absolute right-3 top-1/2 -translate-y-1/2 text-sm text-white/45">{suffix}</span>}
    </div>
  </div>
);

export { AmountField };

/** Lock collateral and mint `synth` in a new position */
const OpenPosition = ({ market, account, synth, feeBps, onOpen }) => {
  const t = useT();
  const enabled = market.collaterals.filter((c) => c.enabled);
  const [collateralAddr, setCollateralAddr] = useState(enabled[0]?.address);
  const [collateralInput, setCollateralInput] = useState('');
  const [mintInput, setMintInput] = useState('');

  useEffect(() => {
    setCollateralInput('');
    setMintInput('');
  }, [synth.ticker]);

  const collateral = enabled.find((c) => c.address === collateralAddr) || enabled[0];
  const balance = account && collateral ? num(account.balances[collateral.address.toLowerCase()], collateral.decimals) : 0;

  const preview = useMemo(() => {
    if (!collateral) return null;
    const collateralPrice = collateral.price ? num(collateral.price) : null;
    const price = synth.price ? num(synth.price) : null;
    const amount = parseFloat(collateralInput) || 0;
    const mint = parseFloat(mintInput) || 0;
    const debt = mint;
    const debtUsd = price !== null ? debt * price : null;
    // the mint fee is paid out of the collateral, the debt is exactly the minted amount
    const feeUsd = debtUsd !== null ? (debtUsd * feeBps) / 10000 : 0;
    const collateralUsd = collateralPrice !== null ? amount * collateralPrice - feeUsd : null;
    const maxMint =
      collateralPrice !== null && price
        ? ((amount * collateralPrice * 10000) / collateral.mcrBps / (price * (1 + feeBps / collateral.mcrBps))) * 0.999
        : 0;
    return {
      collateralPrice,
      price,
      collateralUsd,
      feeCollateral: collateralPrice ? feeUsd / collateralPrice : 0,
      debt,
      debtUsd,
      maxMint,
      ratio: collateralUsd !== null && debtUsd !== null ? ratioBps(collateralUsd, debtUsd) : null,
      liqPrice: collateralUsd !== null ? liquidationPrice(collateralUsd, debt, collateral.liquidationRatioBps) : null,
    };
  }, [collateral, synth.price, collateralInput, mintInput, feeBps]);

  if (!collateral) {
    return <p className="text-sm text-white/55">{t('cynths.noCollateral')}</p>;
  }

  const minDebtUsd = num(market.minDebtUsd);
  const collateralUnits = toUnits(collateralInput, collateral.decimals);
  const mintUnits = mintInput ? toUnits(mintInput, 18) : null;

  let problem = null;
  if (!account) problem = t('cynths.connectFirst');
  else if (!synth.mintingEnabled) problem = t('cynths.mintingPaused');
  else if (preview.collateralPrice === null || preview.price === null) problem = t('cynths.priceUnavailable');
  else if (!collateralUnits) problem = t('cynths.enterCollateral');
  else if (parseFloat(collateralInput) > balance) problem = t('cynths.insufficientBalance');
  else if (mintInput && !mintUnits) problem = t('cynths.invalidAmount');
  else if (mintUnits && preview.debtUsd < minDebtUsd) problem = t('cynths.minDebt', { min: formatUsd(minDebtUsd) });
  else if (mintUnits && preview.ratio < collateral.mcrBps) problem = t('cynths.belowMcr', { mcr: formatPct(collateral.mcrBps) });

  const submit = async () => {
    await onOpen(collateral.address, synth.id, collateralUnits, mintUnits || 0);
    setCollateralInput('');
    setMintInput('');
  };

  return (
    <div className="space-y-4">
      <div>
        <span className="lb-label">{t('cynths.collateral')}</span>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {enabled.map((c) => (
            <button
              key={c.address}
              type="button"
              onClick={() => setCollateralAddr(c.address)}
              className={`rounded-lg px-3 py-2 text-left text-sm transition ${
                c.address === collateral.address
                  ? 'border border-brand-cyan/45 bg-brand-cyan/10 text-white'
                  : 'border border-white/10 bg-white/[0.03] text-white/70 hover:border-white/25'
              }`}
            >
              <span className="notranslate block font-bold">{c.symbol}</span>
              <span className="block text-xs text-white/45">
                {t('cynths.mcrShort')} {formatPct(c.mcrBps)} · {t('cynths.liqShort')} {formatPct(c.liquidationRatioBps)}
              </span>
            </button>
          ))}
        </div>
      </div>

      <AmountField
        label={t('cynths.collateralAmount')}
        balanceLabel={`${t('cynths.balance')}: ${formatAmount(balance)} ${collateral.symbol}`}
        value={collateralInput}
        onChange={setCollateralInput}
        onMax={() => setCollateralInput(toInput(balance, collateral.decimals))}
        suffix={collateral.symbol}
      />

      <AmountField
        label={t('cynths.mintAmount')}
        balanceLabel={preview.maxMint > 0 ? `${t('cynths.maxSafe')}: ${formatAmount(preview.maxMint, 6)}` : null}
        value={mintInput}
        onChange={setMintInput}
        onMax={preview.maxMint > 0 ? () => setMintInput(toInput(preview.maxMint, 8)) : null}
        suffix={synth.ticker}
      />

      <div className="lb-tile-soft grid grid-cols-2 gap-3 p-4 text-sm">
        <div>
          <p className="text-white/50">{t('cynths.collateralValue')}</p>
          <p className="notranslate lb-num font-bold text-white">{formatUsd(preview.collateralUsd)}</p>
        </div>
        <div>
          <p className="text-white/50">{t('cynths.debt')}</p>
          <p className="notranslate lb-num font-bold text-white">
            {formatAmount(preview.debt, 6)} {synth.ticker}
          </p>
          {preview.feeCollateral > 0 && (
            <p className="notranslate text-xs text-white/45">
              {t('cynths.feeInCollateral', { fee: formatPct(feeBps) })}: {formatAmount(preview.feeCollateral, 6)} {collateral.symbol}
            </p>
          )}
        </div>
        <div>
          <p className="text-white/50">{t('cynths.collateralRatio')}</p>
          <p className={`notranslate lb-num font-bold ${preview.ratio === null ? 'text-white/70' : ratioTone(preview.ratio, collateral.mcrBps, collateral.liquidationRatioBps)}`}>
            {preview.ratio === null ? 'N/A' : formatRatio(preview.ratio)}
          </p>
        </div>
        <div>
          <p className="text-white/50">{t('cynths.liquidationPrice', { ticker: synth.ticker })}</p>
          <p className="notranslate lb-num font-bold text-white">{preview.liqPrice ? formatUsd(preview.liqPrice) : '—'}</p>
        </div>
      </div>

      <p className="text-xs text-brand-periwinkle">
        {feeBps === 0
          ? t('cynths.feeWaived')
          : t('cynths.feeHint', { fee: formatPct(feeBps), amount: formatAmount(num(market.cranniPremium, 8), 0) })}
      </p>

      {problem && collateralInput !== '' && <p className="text-sm text-amber-300">{problem}</p>}

      <button className="lb-btn lb-btn-primary w-full" disabled={!!problem} onClick={submit}>
        {mintUnits ? t('cynths.openAndMint', { ticker: synth.ticker }) : t('cynths.openPosition')}
      </button>
    </div>
  );
};

export default OpenPosition;
